import React from 'react';
import { Migas, type Miga } from './Migas';

/**
 * Chrome compartido de las pantallas del shell nuevo —
 * ADR-2026-08-03-chrome-de-pantalla-compartido.
 *
 * Las pantallas de modo, el asistente, `/plantillas` y el estudio de video tenían
 * el mismo marco y el mismo encabezado escritos cuatro veces. Retocar el borde o el
 * aire del breadcrumb obligaba a acordarse de los cuatro archivos, y el que se
 * olvidaba quedaba distinto. Acá viven una sola vez.
 *
 * Hay **dos variantes**, y la diferencia no es cosmética:
 *
 * - `panel` — la pantalla se abre ENCIMA del contenido (`fixed`, con su propio
 *   scroll): modos y asistente. Lleva el marco flotante y un encabezado con su
 *   propio padding.
 * - `seccion` — la pantalla ES el contenido, dentro de la columna del shell:
 *   `/plantillas` y el estudio de video. No lleva marco —lo pone el shell— y su
 *   encabezado es sólo el breadcrumb arriba del `<h1>` propio de la pantalla, que
 *   sigue tomando el tamaño y el aire de la regla compartida de `.shell-seccion`
 *   (ADR-2026-08-03-titulos-secciones-igual-que-inicio).
 */

/**
 * Geometría del marco flotante, igual a la de la sidebar
 * (ADR-2026-08-03-paneles-de-modo-marco-de-la-sidebar): 8px de aire arriba, a la
 * derecha y abajo, y las cuatro esquinas en 16px.
 *
 * Todo va con `lg:`, que en estas pantallas es exactamente "dentro del shell
 * nuevo": por debajo de 1024px se montan en `DashboardLayout`, donde la geometría
 * vieja —pegado a los bordes, redondeado sólo del lado izquierdo— es la correcta.
 * El aire de la IZQUIERDA no está acá: lo da `sidebarWidth`, que es margen + ancho
 * + margen, o sea el borde derecho de la sidebar.
 *
 * Las esquinas se declaran una por una y no con `lg:rounded-2xl`: el atajo es la
 * propiedad corta `border-radius` y tiene que ganarle a dos largas
 * (`rounded-tl-[26px]`, `rounded-bl-[26px]`) que las pantallas traen en su clase
 * base — quién gana depende del orden en la hoja de estilos, y no vale la pena
 * correr esa carrera.
 */
export const MARCO_PANTALLA =
  'lg:inset-y-2 lg:right-2 lg:rounded-tl-2xl lg:rounded-tr-2xl lg:rounded-br-2xl lg:rounded-bl-2xl';

/** El borde del marco. Aparte de la geometría porque el backdrop del asistente usa
 *  la forma pero no el borde: es un velo, no una superficie. */
export const BORDE_PANTALLA = 'lg:border lg:border-black/[0.06]';

export function EncabezadoPantalla({
  migas,
  variante = 'panel',
  respaldo,
  className = '',
  children,
}: {
  /** Niveles del breadcrumb. Vacío o ausente → se dibuja `respaldo`. */
  migas?: Miga[];
  variante?: 'panel' | 'seccion';
  /** Qué dibujar cuando no hay migas — hoy, el botón "Volver" de las pantallas
   *  que no son un modo (plantillas, poses). */
  respaldo?: React.ReactNode;
  className?: string;
  /** Acciones a la derecha del encabezado: el pill de "Generar", el toggle
   *  Imagen/Video. Cada pantalla arma las suyas; acá sólo tienen lugar. */
  children?: React.ReactNode;
}) {
  const contenido =
    migas && migas.length > 0 ? <Migas items={migas} /> : (respaldo ?? null);

  // En una sección el breadcrumb no necesita fila propia: va suelto arriba del
  // `<h1>`, que ya trae su aire. `hidden lg:inline-flex` porque por debajo de
  // 1024px estas pantallas conservan su botón "Volver".
  if (variante === 'seccion') {
    if (!migas || migas.length === 0) return null;
    return <Migas items={migas} className={`hidden lg:inline-flex mb-4 ${className}`} />;
  }

  // El encabezado va SIEMPRE en el flujo — ADR-2026-08-04-el-breadcrumb-no-se-pega.
  // Tuvo una variante flotante (`sticky` + fondo propio, o transparente con el
  // breadcrumb en una pastilla blanca) para el paso `configure` de los modos
  // full-screen; se sacó porque el breadcrumb viajando con el scroll se leía como un
  // elemento que se mueve solo. Ahora scrollea junto con el resto de la pantalla.
  return (
    <header className={`relative shrink-0 px-6 pt-5 pb-3 ${className}`}>
      {contenido}
      {children}
    </header>
  );
}

export default EncabezadoPantalla;
